import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { UpdateAccountInput } from '../types/api.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アカウント一覧をTanStack Query経由で取得。ポータル登録済みアカウントの全件リストを返す
 * @graph-connects /accounts [calls] アカウント一覧取得API
 * @graph-connects accountApi.getAccounts [calls] アカウント一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useAccounts() {
  return useQuery({
    queryKey: queryKeys.accounts.list(),
    queryFn: () => accountApi.getAccounts(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ユーザー検索をTanStack Query経由で実行。2文字以上の入力でオートサジェスト候補を返す
 * @graph-connects /accounts/search [calls] ユーザー検索API
 * @graph-connects accountApi.searchUsers [calls] ユーザー検索APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUserSearch(query: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.accounts.search(query),
    queryFn: () => accountApi.searchUsers(query),
    enabled: enabled && query.length >= 2,
    staleTime: 30 * 1000, // 30秒間キャッシュ
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アカウント更新ミューテーション。メール指定でロール等のアカウント情報をPUT更新し、一覧キャッシュを無効化
 * @graph-connects /accounts/:email [calls] アカウント更新API（PUT）
 * @graph-connects accountApi.updateAccount [calls] アカウント更新APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, data }: { email: string; data: UpdateAccountInput }) =>
      accountApi.updateAccount(email, data),
    onSuccess: () => {
      // アカウント一覧を再取得
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アカウント削除ミューテーション。メール指定でアカウントをDELETE削除し、一覧キャッシュを無効化
 * @graph-connects /accounts/:email [calls] アカウント削除API（DELETE）
 * @graph-connects accountApi.deleteAccount [calls] アカウント削除APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => accountApi.deleteAccount(email),
    onSuccess: () => {
      // アカウント一覧を再取得
      void queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
    },
  });
}
